const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile-script'
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
const TURNSTILE_CONTAINER_ID = 'cloudflare-turnstile-container'
const DEFAULT_TIMEOUT_MS = 13000
let turnstileExecutionPromise: Promise<string> | null = null

interface TurnstileConfig {
  enabled?: boolean
  siteKey?: string
}

interface TurnstileExecutionOptions extends TurnstileConfig {
  timeoutMs?: number
}

const getTurnstileContainer = (): HTMLDivElement => {
  const existingContainer = document.getElementById(TURNSTILE_CONTAINER_ID)
  if (existingContainer instanceof HTMLDivElement) {
    return existingContainer
  }

  const container = document.createElement('div')
  container.id = TURNSTILE_CONTAINER_ID
  container.style.display = 'none'
  document.body.appendChild(container)
  return container
}

const loadTurnstileScript = (): Promise<void> => {
  if (typeof window === 'undefined') {
    return Promise.resolve()
  }

  if (window.turnstile) {
    return Promise.resolve()
  }

  const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null
  if (existingScript) {
    if (existingScript.dataset.loaded === 'true') {
      return Promise.resolve()
    }

    if (existingScript.dataset.failed === 'true') {
      existingScript.remove()
    } else {
      return new Promise((resolve, reject) => {
        existingScript.addEventListener('load', () => resolve(), { once: true })
        existingScript.addEventListener('error', () => reject(new Error('Verification unavailable')), { once: true })
      })
    }
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = TURNSTILE_SCRIPT_ID
    script.src = TURNSTILE_SCRIPT_SRC
    script.async = true
    script.defer = true
    script.addEventListener(
      'load',
      () => {
        script.dataset.loaded = 'true'
        resolve()
      },
      { once: true }
    )
    script.addEventListener(
      'error',
      () => {
        script.dataset.failed = 'true'
        script.remove()
        reject(new Error('Verification unavailable'))
      },
      { once: true }
    )
    document.head.appendChild(script)
  })
}

export const preloadTurnstile = async ({ enabled, siteKey }: TurnstileConfig): Promise<void> => {
  if (!enabled || !siteKey || typeof window === 'undefined') {
    return
  }

  await loadTurnstileScript()
}

const executeTurnstile = (siteKey: string, timeoutMs: number): Promise<string> =>
  new Promise((resolve, reject) => {
    const container = getTurnstileContainer()
    const executionState: {
      widgetId?: string
      timeoutId?: number
      settled?: boolean
    } = {}

    const cleanup = () => {
      if (executionState.timeoutId) {
        window.clearTimeout(executionState.timeoutId)
      }
      window.setTimeout(() => {
        try {
          if (executionState.widgetId) {
            window.turnstile?.remove?.(executionState.widgetId)
          }
        } catch {
          // Turnstile may already have reset/removed the widget after an error.
        }
        container.remove()
      }, 0)
    }

    const finishWithToken = (token: string) => {
      if (executionState.settled) {
        return
      }
      executionState.settled = true
      cleanup()
      resolve(token)
    }

    const finishWithError = (error: Error) => {
      if (executionState.settled) {
        return
      }
      executionState.settled = true
      cleanup()
      reject(error)
    }

    executionState.widgetId = window.turnstile?.render(container, {
      sitekey: siteKey,
      size: 'invisible',
      execution: 'execute',
      callback: token => {
        if (token) {
          finishWithToken(token)
        } else {
          finishWithError(new Error('Verification failed'))
        }
      },
      'error-callback': () => finishWithError(new Error('Verification failed')),
      'expired-callback': () => finishWithError(new Error('Verification expired')),
      'timeout-callback': () => finishWithError(new Error('Verification timed out')),
    })

    if (!executionState.widgetId) {
      finishWithError(new Error('Verification unavailable'))
      return
    }

    executionState.timeoutId = window.setTimeout(() => {
      finishWithError(new Error('Verification timed out'))
    }, timeoutMs)

    try {
      window.turnstile?.execute(executionState.widgetId)
    } catch (error) {
      finishWithError(error instanceof Error ? error : new Error('Verification failed'))
    }
  })

export const getTurnstileToken = async ({
  enabled,
  siteKey,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: TurnstileExecutionOptions): Promise<string | null> => {
  if (!enabled) {
    return null
  }

  if (!siteKey || typeof window === 'undefined') {
    return null
  }

  try {
    await loadTurnstileScript()
    if (!turnstileExecutionPromise) {
      turnstileExecutionPromise = executeTurnstile(siteKey, timeoutMs).finally(() => {
        turnstileExecutionPromise = null
      })
    }

    return await turnstileExecutionPromise
  } catch {
    return null
  }
}
